import type { TickerPosition } from "@bloomberg/octagon-shared";
import { getChangeSeverity } from "~components/Heatmap/util";
import type { HeatmapTileData, HeatmapTileTrend } from "~types/types";
import { formatDecimals } from "~utils/portfolio/formatNumber";

export const getTrend = (percentChange: number): HeatmapTileTrend => {
  if (percentChange > 0) return "positive";
  if (percentChange < 0) return "negative";

  return "neutral";
};

export interface WatchlistHeatmapPosition {
  readonly ticker: string;
  readonly tickerMarketValue: number;
  readonly dayPCTChange: number;
  readonly longCompName: string;
  readonly price: number;
  readonly priceChange1Day: number;
  readonly currency: string;
  readonly shares: number;
}

const mapPositionToTicker = (
  position: TickerPosition
): WatchlistHeatmapPosition => {
  const totalShares =
    position.lots?.reduce((sum, lot) => sum + (lot.shares || 0), 0) || 0;

  return {
    ticker: position.ticker,
    tickerMarketValue: position.marketValue?.value || 0,
    dayPCTChange: position.marketData?.percentChange1Day || 0,
    longCompName: position.marketData?.longName || position.ticker,
    price: position.marketData?.price || 0,
    priceChange1Day: position.marketData?.priceChange1Day || 0,
    currency: position.marketData?.issuedCurrency || "",
    shares: totalShares,
  };
};

export type HeatmapTileDataWithChange = Omit<
  HeatmapTileData<WatchlistHeatmapPosition>,
  "content"
>;

export function prepareHeatmapTiles(
  positions: TickerPosition[],
  sortBy: "PERF" | "HOLD"
): HeatmapTileDataWithChange[] {
  const mapped = positions.map(mapPositionToTicker);
  const lotsAvailable = mapped.some((t) => t.tickerMarketValue > 0);

  const validTickers = lotsAvailable
    ? mapped.filter((t) => t.tickerMarketValue > 0)
    : mapped.map((t) => ({ ...t, tickerMarketValue: 1 }));

  if (validTickers.length === 0) return [];

  validTickers.sort((a, b) => {
    if (sortBy === "PERF") {
      return b.dayPCTChange - a.dayPCTChange;
    }

    return b.tickerMarketValue - a.tickerMarketValue;
  });

  const totalValue = validTickers.reduce(
    (sum, t) => sum + t.tickerMarketValue,
    0
  );

  if (totalValue === 0) return [];

  return validTickers.map((ticker) => {
    const percentChange = ticker.dayPCTChange;
    const severity = getChangeSeverity(percentChange);
    const trend = getTrend(percentChange);
    const size = Number(
      formatDecimals((ticker.tickerMarketValue / totalValue) * 100, 2)
    );

    return {
      ...ticker,
      severity,
      label: ticker.ticker,
      trend,
      size,
    };
  });
}
